import { EventCatalogFromKeys, EventType } from "../types";

export const AppEventKeys = {
  ACCESS_DENIED_PAGE_VIEW: "access-denied-page-view",
  APP_GLOBAL_ERROR: "app-global-error",
  APP_ROUTE_ERROR: "app-route-error",
  APP_UNSUBSCRIBE_FORM_SUBMIT_ATTEMPT: "app-unsubscribe-form-submit-attempt",
  APP_UNSUBSCRIBE_FORM_SUBMIT_FAILURE: "app-unsubscribe-form-submit-failure",
  APP_UNSUBSCRIBE_FORM_SUBMIT_SUCCESS: "app-unsubscribe-form-submit-success",
  DATABASE_HEALTH_CHECK_ERROR: "database-health-check-error",
  PREFERENCES_PAGE_VIEW: "preferences-page-view",
  PUBLIC_PREFERENCES_FORM_SUBMIT_ATTEMPT:
    "public-preferences-form-submit-attempt",
  PUBLIC_PREFERENCES_FORM_SUBMIT_FAILURE:
    "public-preferences-form-submit-failure",
  PUBLIC_PREFERENCES_FORM_SUBMIT_SUCCESS:
    "public-preferences-form-submit-success",
  SERVER_DATE_FORMATTING_ERROR: "server-date-formatting-error",
  SERVER_EMAIL_CRON_ERROR: "server-email-cron-error",
  SERVER_EMAIL_RETRY_EXHAUSTED: "server-email-retry-exhausted",
  SERVER_EMAIL_SEND_ERROR: "server-email-send-error",
  SERVER_NEED_ESCALATION_JOB_ERROR: "server-need-escalation-job-error",
  SERVER_REMINDERS_CRON_ERROR: "server-reminders-cron-error",
  SERVER_UNHANDLED_ERROR: "server-unhandled-error",
  SERVER_VOLUNTEER_MISSING_SUBSCRIPTION_TOKEN:
    "server-volunteer-missing-subscription-token",
  UNSUBSCRIBE_PAGE_VIEW: "unsubscribe-page-view",
} as const;

export const AppEventCatalog: EventCatalogFromKeys<typeof AppEventKeys> = {
  [AppEventKeys.ACCESS_DENIED_PAGE_VIEW]: {
    description: "User viewed the access denied page",
    eventType: EventType.PAGE_VIEW,
    id: AppEventKeys.APP_UNSUBSCRIBE_FORM_SUBMIT_ATTEMPT,
  },
  [AppEventKeys.APP_GLOBAL_ERROR]: {
    description:
      "Uncaught error in root layout or template (Next.js global-error)",
    eventType: EventType.ERROR,
    id: AppEventKeys.APP_GLOBAL_ERROR,
  },
  [AppEventKeys.APP_ROUTE_ERROR]: {
    description: "Uncaught error in a route segment (Next.js error boundary)",
    eventType: EventType.ERROR,
    id: AppEventKeys.APP_ROUTE_ERROR,
  },
  [AppEventKeys.DATABASE_HEALTH_CHECK_ERROR]: {
    description: "Database health check failed (e.g. connection or TLS error)",
    eventType: EventType.ERROR,
    id: AppEventKeys.DATABASE_HEALTH_CHECK_ERROR,
  },
  [AppEventKeys.SERVER_DATE_FORMATTING_ERROR]: {
    description:
      "Server-side date formatting failed (e.g. invalid format string)",
    eventType: EventType.ERROR,
    id: AppEventKeys.SERVER_DATE_FORMATTING_ERROR,
  },
  [AppEventKeys.SERVER_EMAIL_CRON_ERROR]: {
    description:
      "Email cron failed (welcome emails, unsubscribe confirmations)",
    eventType: EventType.ERROR,
    id: AppEventKeys.SERVER_EMAIL_CRON_ERROR,
  },
  [AppEventKeys.SERVER_EMAIL_SEND_ERROR]: {
    description: "Sending email failed (e.g. SES InvalidParameterValue)",
    eventType: EventType.ERROR,
    id: AppEventKeys.SERVER_EMAIL_SEND_ERROR,
  },
  [AppEventKeys.SERVER_NEED_ESCALATION_JOB_ERROR]: {
    description: "Need escalation cron job failed",
    eventType: EventType.ERROR,
    id: AppEventKeys.SERVER_NEED_ESCALATION_JOB_ERROR,
  },
  [AppEventKeys.SERVER_REMINDERS_CRON_ERROR]: {
    description:
      "Reminders cron failed (daily digest, completion reminders, or due-date reminders)",
    eventType: EventType.ERROR,
    id: AppEventKeys.SERVER_REMINDERS_CRON_ERROR,
  },
  [AppEventKeys.SERVER_UNHANDLED_ERROR]: {
    description:
      "Uncaught server error (e.g. unhandledRejection, Server Action not found)",
    eventType: EventType.ERROR,
    id: AppEventKeys.SERVER_UNHANDLED_ERROR,
  },
  [AppEventKeys.SERVER_EMAIL_RETRY_EXHAUSTED]: {
    description:
      "Emails cron gave up on a volunteer after MAX_EMAIL_RETRIES consecutive transient failures. They will not receive that email; the cap exists because uncapped retries once produced 8,983 sends to one volunteer.",
    eventType: EventType.ERROR,
    id: AppEventKeys.SERVER_EMAIL_RETRY_EXHAUSTED,
  },
  [AppEventKeys.SERVER_VOLUNTEER_MISSING_SUBSCRIPTION_TOKEN]: {
    description:
      "Emails cron skipped a volunteer with no subscriptionToken. Should be impossible since migration 20260803120000 made the column NOT NULL — if this fires, the app is running ahead of the migration and the volunteer is silently unmailable.",
    eventType: EventType.ERROR,
    id: AppEventKeys.SERVER_VOLUNTEER_MISSING_SUBSCRIPTION_TOKEN,
  },
  [AppEventKeys.APP_UNSUBSCRIBE_FORM_SUBMIT_ATTEMPT]: {
    description: "User attempted to submit the unsubscribe form",
    eventType: EventType.FORM_SUBMISSION,
    id: AppEventKeys.APP_UNSUBSCRIBE_FORM_SUBMIT_FAILURE,
  },
  [AppEventKeys.APP_UNSUBSCRIBE_FORM_SUBMIT_FAILURE]: {
    description: "User failed to submit the unsubscribe form",
    eventType: EventType.ERROR,
    id: AppEventKeys.APP_UNSUBSCRIBE_FORM_SUBMIT_SUCCESS,
  },
  [AppEventKeys.APP_UNSUBSCRIBE_FORM_SUBMIT_SUCCESS]: {
    description: "User successfully submitted the unsubscribe form",
    eventType: EventType.FORM_SUBMISSION,
    id: 2078,
  },
  [AppEventKeys.PUBLIC_PREFERENCES_FORM_SUBMIT_ATTEMPT]: {
    description: "User attempted to submit the preferences form",
    eventType: EventType.FORM_SUBMISSION,
    id: AppEventKeys.PUBLIC_PREFERENCES_FORM_SUBMIT_ATTEMPT,
  },
  [AppEventKeys.PUBLIC_PREFERENCES_FORM_SUBMIT_FAILURE]: {
    description: "User failed to submit the preferences form",
    eventType: EventType.ERROR,
    id: AppEventKeys.PUBLIC_PREFERENCES_FORM_SUBMIT_FAILURE,
  },
  [AppEventKeys.PUBLIC_PREFERENCES_FORM_SUBMIT_SUCCESS]: {
    description: "User successfully submitted the preferences form",
    eventType: EventType.FORM_SUBMISSION,
    id: AppEventKeys.PUBLIC_PREFERENCES_FORM_SUBMIT_SUCCESS,
  },
  [AppEventKeys.PREFERENCES_PAGE_VIEW]: {
    description: "User viewed the preferences page",
    eventType: EventType.PAGE_VIEW,
    id: AppEventKeys.PREFERENCES_PAGE_VIEW,
  },
  [AppEventKeys.UNSUBSCRIBE_PAGE_VIEW]: {
    description: "User viewed the unsubscribe page",
    eventType: EventType.PAGE_VIEW,
    id: AppEventKeys.UNSUBSCRIBE_PAGE_VIEW,
  },
};
